import React, { useMemo } from 'react';
import { 
  Paper,
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  IconButton,
  Divider,
  Stack,
  useTheme,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import { FULL_DATA } from '../Data/FullData';

const LocationCircuits = ({ selectedLocation, onClose }) => {
  const theme = useTheme();

  const circuits = useMemo(() => {
    if (!selectedLocation) return [];
    return FULL_DATA.DATA.filter(
      circuit => circuit.Abbreviation === selectedLocation.abbreviation
    );
  }, [selectedLocation]);

  const totals = useMemo(() => {
    if (!circuits.length) return { cost: 0, bandwidth: 0 };
    
    return circuits.reduce((acc, circuit) => {
      const cost = typeof circuit['MRC w/tax & fees'] === 'string' 
        ? parseFloat(circuit['MRC w/tax & fees'].replace(/[^0-9.-]+/g, '')) || 0
        : circuit['MRC w/tax & fees'] || 0;
        
      const speed = typeof circuit['Speed (mbps)'] === 'string'
        ? parseFloat(circuit['Speed (mbps)'].replace(/[^0-9.-]+/g, '')) || 0
        : circuit['Speed (mbps)'] || 0;

      return {
        cost: acc.cost + cost,
        bandwidth: acc.bandwidth + speed
      };
    }, { cost: 0, bandwidth: 0 });
  }, [circuits]);

  if (!selectedLocation) return null;

  if (!circuits.length) {
    return (
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" color="text.primary">
            No circuits found for {selectedLocation.businessName}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={3} sx={{ p: 3, maxWidth: 'xl', mx: 'auto' }}>
      <Stack spacing={3}>
        {/* Header Section */}
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} 
             justifyContent="space-between" alignItems={{ xs: 'start', md: 'center' }} gap={2}>
          <Typography variant="h5" fontWeight="bold" color="primary">
            {selectedLocation.businessName}
          </Typography>
          
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ xs: 'start', md: 'center' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <AccountBalanceWalletIcon color="success" />
              <Typography variant="body1" color="text.secondary">
                Total Monthly Cost:{' '}
                <Typography component="span" variant="h6" color="success.main" fontWeight="bold">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                  }).format(totals.cost)}
                </Typography>
              </Typography>
            </Box>
            
            <Box display="flex" alignItems="center" gap={1}>
              <SignalCellularAltIcon color="primary" />
              <Typography variant="body1" color="text.secondary">
                Total Bandwidth:{' '}
                <Typography component="span" variant="h6" color="primary" fontWeight="bold">
                  {totals.bandwidth} Mbps
                </Typography>
              </Typography>
            </Box>

            <IconButton 
              onClick={onClose} 
              size="small" 
              aria-label="close"
              sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}
            >
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>

        <Divider />

        {/* Circuits Cards Layout */}
        <Box sx={{ 
          maxHeight: '600px', 
          overflow: 'auto',
          pr: 1,
          '&::-webkit-scrollbar': {
            width: '8px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: '4px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '4px'
          }
        }}>
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 3,
            '& > *': {
              flexGrow: 1,
              flexBasis: {
                xs: '100%',
                md: 'calc(50% - 12px)',
                lg: 'calc(33.333% - 16px)'
              },
              minWidth: {
                xs: '100%',
                md: 'calc(50% - 12px)',
                lg: 'calc(33.333% - 16px)'
              },
              maxWidth: {
                xs: '100%',
                md: 'calc(50% - 12px)',
                lg: 'calc(33.333% - 16px)'
              }
            }
          }}>
            {circuits.map((circuit, index) => (
              <Card key={index} sx={{ display: 'flex', flexDirection: 'column' }}>
                <CardHeader
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    py: 2
                  }}
                  title={
                    <Typography variant="subtitle1" fontWeight="medium">
                      {circuit['Service Type'] || 'Circuit'} - {circuit.Provider || 'Unknown Provider'}
                    </Typography>
                  }
                  action={
                    circuit['Status'] && (
                      <Chip 
                        label={circuit['Status']}
                        size="small"
                        color={circuit['Status'].toLowerCase() === 'active' ? 'success' : 'default'}
                        sx={{ color: 'white', borderColor: 'white' }}
                      />
                    )
                  }
                />
                <CardContent sx={{ flexGrow: 1 }}>
                  <Stack spacing={2}>
                    {[
                      ['Service/Circuit ID', 'Service/Circuit ID'],
                      ['Speed', 'Speed (mbps)', value => `${value} Mbps`],
                      ['Monthly Cost', 'MRC w/tax & fees', value => 
                        typeof value === 'string' 
                          ? value
                          : new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD'
                            }).format(value || 0)
                      ],
                      ['Billing Account', 'Billing Account#'],
                      ['Expiration', 'Exp Date']
                    ].map(([label, key, formatter]) => (
                      <Box key={key} 
                           sx={{
                             display: 'flex',
                             justifyContent: 'space-between',
                             alignItems: 'center',
                             pb: 1,
                             borderBottom: 1,
                             borderColor: 'divider',
                             '&:last-child': {
                               borderBottom: 0,
                               pb: 0
                             }
                           }}>
                        <Typography variant="body2" color="text.secondary">
                          {label}:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatter 
                            ? formatter(circuit[key]) 
                            : circuit[key] || 'N/A'}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>

                  {circuit.Notes && (
                    <Box mt={2}>
                      <Typography variant="body2" color="text.secondary" fontWeight="medium">
                        Notes:
                      </Typography>
                      <Typography variant="body2" color="text.secondary" mt={0.5}>
                        {circuit.Notes}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </Stack>
    </Paper>
  );
};

export default LocationCircuits;